import { Modal } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  getAllDepartmentList,
  getDepartmentListForsignupFlow,
} from '../redux/commonSlice';
import { useAppSelector, RootState } from '../redux/store';
import Loading from './Loading';
import CustomButton from './CustomButton';
import { getDepartmentList } from '../redux/ProductionSlice';

/**
 * Render a modal for selecting a department from a list, allowing for department selection and form submission.
 * @example
 * DepartmentsModal({ open, handleClose, handleClick, setSelectedDeptId })
 * Renders a modal for department selection.
 * @param {boolean} open - Flag indicating if the modal should be open.
 * @param {function} handleClose - Function to close the modal.
 * @param {function} handleClick - Function to handle the submit action.
 * @param {function} setSelectedDeptId - Function to set the selected department ID.
 * @returns {JSX.Element} A modal component with department selection functionality.
 * @description
 *   - Fetches the list of departments using `getAllDepartmentList` and `getDepartmentListForsignupFlow`.
 *   - Updates the local state and invokes the parent function when a department is selected.
 *   - Ensures the "Continue" button is disabled until a department is selected.
 */
const DepartmentsCreate = ({
  open,
  handleClose,
  handleClick,
  setSelectedDeptId,
  showDeptSignup,
  isCreateStudio,
}: any) => {
  const { deptList, departmentSignUplist } = useAppSelector(
    (state: RootState) => state.common,
  );
  const { departmentlist } = useAppSelector(
    (state: RootState) => state.production,
  );
  const [selectedDeptId, setSelectedDeptIdState] = useState<string | null>(
    null,
  ); // Track selected department

  useEffect(() => {
    getAllDepartmentList(); // Fetch department list
    getDepartmentListForsignupFlow();
    if (isCreateStudio) {
      getDepartmentList(undefined, 'studios');
    }
  }, []);

  // Removed the useEffect that was auto-selecting the first department

  const handleDeptChange = (deptId: string) => {
    setSelectedDeptIdState(deptId); // Update state for selected department
    setSelectedDeptId(deptId); // Call parent function to set department ID
  };

  const deptListData = showDeptSignup
    ? departmentSignUplist
    : isCreateStudio
    ? departmentlist
    : deptList?.departments;

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="modal-main"
      >
        <div className="modal-dialog med">
          <div className="modal-content">
            <div className="modal-title d-flex flex-between align-center">
              <p className="title text-gray_700 fw500">Select Department</p>
              <button className="blank-btn" onClick={handleClose}>
                <i className="cross-icon"></i>
              </button>
            </div>

            <div className="modal-body pt-20 body-scroll-sm departments-sel">
              <div className="row">
                {deptListData?.length ? (
                  deptListData.map((dept: any) => (
                    <div className="col-lg-6" id={dept?._id} key={dept?._id}>
                      <div className="radio mb-20">
                        <input
                          type="radio"
                          name="create"
                          value={dept?._id}
                          onChange={() => handleDeptChange(dept?._id)}
                          checked={selectedDeptId === dept?._id}
                        />
                        <label>
                          <span>{dept?.deptName}</span>
                        </label>
                      </div>
                    </div>
                  ))
                ) : (
                  <Loading type="div" height="full" />
                )}
              </div>
            </div>

            <div className="modal-actions">
              <div>
                <CustomButton
                  id=""
                  text="Cancel"
                  size="medium"
                  variant="outlined"
                  showIcon={false}
                  onClick={handleClose}
                  className="min_width_btn"
                />
              </div>
              <div>
                <CustomButton
                  id=""
                  text="Continue"
                  size="medium"
                  variant="contained"
                  showIcon={false}
                  onClick={() => {
                    handleClick();
                    handleClose();
                  }}
                  className="min_width_btn"
                  disabled={!selectedDeptId} // Disable if no department is selected
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default DepartmentsCreate;
